// Lazy-loads products from /api/products into the #products table as the
// page is scrolled. Each page is small (limit=20) and fetched only when the
// user nears the bottom of the list, until the server reports hasMore=false.

const PAGE_SIZE = 20;
const SEARCH_LIMIT = 100;
const SEARCH_DELAY_MS = 250;

// Static-site localization from public/i18n.js (loads before this script).
const { t, tCount } = window.I18N;

const tbody = document.getElementById('product-rows');
const status = document.getElementById('status');
const categorySelect = document.getElementById('category');
const searchInput = document.getElementById('search');

let cursor = null;
let hasMore = true;
let loading = false;
let currentTypeId = '';
let currentQuery = '';
let searchTimer = null;
let propertyColumns = [];
let initialized = false;
// Property ids whose property_type is boolean. Their values render as a green
// check or red cross instead of raw text.
let booleanPropertyIds = new Set();
// Active column sort: key is a base column key or a property id; '' restores
// the server's default (brand, model, id) order. Sorting is applied
// server-side so lazily loaded pages continue the sorted sequence.
let sortKey = '';
let sortDir = 'asc';
// Set while the next listing load must re-fetch page 1 after a sort change
// and reconcile the existing rows instead of wiping the table.
let sortReload = false;
// Bumped by every restart (sort change, category change, search reset); a
// response that lands after the bump is stale and must not be applied, or an
// in-flight scroll load would append old-order rows behind the reload.
let loadGen = 0;
// Active property filters, at most one per property. Each entry is
// { id, op, raw }: id is the property id (UUID), op is 'eq' (value equals),
// 'set' (property present), or 'unset' (property absent), and raw is the
// URL-side value string for 'eq' (typed on the way out to the API). The
// filters are AND-ed server-side via the property_filters parameter and
// reflected in the URL as filter=<id>:<value>, has=<id>, missing=<id>.
let activeFilters = [];
// The filter currently being edited in the builder (set by clicking a
// chip's label), or null while composing a new one. Apply replaces exactly
// this chip; cancel discards.
let editingFilter = null;
// True when /api/session reports a valid login session: rows then get a
// hover-revealed action column (delete + AI completion) like the admin page,
// and every cell becomes editable like the tables on the admin page.
let authenticated = false;
// All product types (id + title), filled by loadTypes. Used to map the
// displayed type title back to the id the PUT /api/products/{id} endpoint
// expects while editing a row's type cell.
let productTypes = [];

const BASE_COLUMNS = [
  { key: 'brand', label: t('col.brand') },
  { key: 'model', label: t('col.model') },
  { key: 'type', label: t('col.type') },
];

// Every rendered column, so header building and stale-sort cleanup agree.
function currentColumns() {
  return BASE_COLUMNS
    .concat(propertyColumns.map(c => ({ key: c.id, label: c.name })))
    .concat([{ key: 'listings', label: t('col.listings'), sortable: false }]);
}

function renderHeader() {
  const headRow = document.getElementById('product-head');
  headRow.textContent = '';
  for (const col of currentColumns()) {
    const th = document.createElement('th');
    if (col.sortable === false) {
      // Not server-sortable (the listings column): a plain header.
      th.textContent = col.label;
      headRow.appendChild(th);
      continue;
    }
    th.classList.add('sortable');
    th.tabIndex = 0;
    th.dataset.key = col.key;
    const label = document.createElement('span');
    label.textContent = col.label;
    const arrow = document.createElement('span');
    arrow.className = 'sort-arrow';
    th.append(label, arrow);
    th.addEventListener('click', () => onSortClick(col.key));
    th.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onSortClick(col.key);
      }
    });
    headRow.appendChild(th);
  }
  if (authenticated) {
    const actions = document.createElement('th');
    actions.className = 'cell-actions';
    headRow.appendChild(actions);
  }
  updateSortIndicators();
}

// Reflect the active sort on the header: aria-sort and an arrow marker on
// the sorted column, nothing elsewhere.
function updateSortIndicators() {
  for (const th of document.getElementById('product-head').children) {
    const active = th.dataset.key === sortKey;
    if (active) {
      th.setAttribute('aria-sort', sortDir === 'asc' ? 'ascending' : 'descending');
    } else {
      th.removeAttribute('aria-sort');
    }
    const arrow = th.querySelector('.sort-arrow');
    if (arrow) arrow.textContent = active ? (sortDir === 'asc' ? '\u25B4' : '\u25BE') : '';
  }
}

// Click a header: first click sorts ascending, another click flips the
// direction, a different column restarts at ascending. Every change reloads
// so both the listing and the search flow honor the sort server-side, and is
// mirrored into the URL (?sort=/?dir=) as its own history entry. The reload
// re-fetches page 1 and reconciles the DOM (no wipe), so rows the new order
// still shows are kept untouched.
function onSortClick(key) {
  if (sortKey === key) {
    sortDir = sortDir === 'asc' ? 'desc' : 'asc';
  } else {
    sortKey = key;
    sortDir = 'asc';
  }
  updateSortIndicators();
  writeSortParams(false);
  reloadForSort();
}

// Refetch page 1 of the current filter/sort without clearing the table, so
// loadPage can diff the existing rows against the new order. Only a category
// change (different columns) justifies a full wipe via resetAndReload().
function reloadForSort() {
  sortReload = true;
  cursor = null;
  hasMore = true;
  loading = false;
  loadGen++;
  loadPage();
}

// Map a boolean property's raw value to its display marker, or null when the
// value is absent (so the caller keeps the '-' placeholder).
function boolMarker(value) {
  if (value === null || value === undefined || value === '') return null;
  const t = String(value).trim();
  return /^(true|1|t|yes|y)$/i.test(t) ? '\u2713' : '\u2717';
}

// Marker shown in a property cell the AI enrichment agent already looked up
// without finding a verifiable value: a red question mark, visually distinct
// from the plain dash that means "no value looked up yet".
const ATTEMPTED_MARKER = '?';

// Marker shown in a property cell recorded as not applicable to the product
// (the AI agent ruled the property out, so no value will ever be looked up).
const NOT_APPLICABLE_MARKER = 'N/A';

// The placeholder cell text for a property without a value: the not-applicable
// marker when the property was ruled out for the product, the red question
// mark when a previous AI lookup failed, the dash otherwise.
function emptyCellMarker(p, c) {
  if (p.statuses && p.statuses[c.id] === 'not_applicable') return NOT_APPLICABLE_MARKER;
  return p.attempts && p.attempts[c.id] ? ATTEMPTED_MARKER : '-';
}

// The tag label for one listing link: the site's host without a leading
// www. (e.g. https://www.louis.de/x → louis), so the tags read like
// [louis] [amazon] [alpinestars].
function listingLabel(url) {
  try {
    let host = new URL(url).hostname;
    if (host.startsWith('www.')) host = host.slice(4);
    return host;
  } catch {
    return url;
  }
}

// The rightmost cell of a row: one clickable tag per storefront listing link
// of the product (the pages the agent spotted it on), each navigating to the
// product listing of that website.
function buildListingsCell(p) {
  const td = document.createElement('td');
  td.className = 'cell-listings';
  for (const url of p.listings || []) {
    const a = document.createElement('a');
    a.className = 'listing-tag';
    a.textContent = listingLabel(url);
    a.href = url;
    a.target = '_blank';
    a.rel = 'noopener';
    td.appendChild(a);
  }
  return td;
}

// Compute the ordered display values of a product row so two rows can be
// compared cheaply and identical rows can be left untouched in the DOM.
function productCells(p) {
  const cells = [];
  for (const key of ['brand', 'model', 'type']) {
    cells.push(p[key] || '');
  }
  for (const c of propertyColumns) {
    const props = p.properties;
    if (props && (c.id in props)) {
      const v = props[c.id];
      cells.push(booleanPropertyIds.has(c.id) ? (boolMarker(v) || '-') : v);
    } else {
      cells.push(emptyCellMarker(p, c));
    }
  }
  // The listings cell's diff text: the tag labels, so a link set change
  // triggers the in-place cell rebuild.
  cells.push((p.listings || []).map(listingLabel).join(' '));
  return cells;
}

// The CSS class for a property cell given its display text: the boolean
// markers keep their true/false/none colors, the failed-lookup question mark
// gets the red attempted style, the not-applicable marker its own muted
// style, everything else is unstyled.
function propertyCellClass(cell, c) {
  if (cell === ATTEMPTED_MARKER) return 'prop-attempted';
  if (cell === NOT_APPLICABLE_MARKER) return 'prop-na';
  if (c && booleanPropertyIds.has(c.id)) {
    return cell === '\u2713' ? 'prop-true'
      : cell === '\u2717' ? 'prop-false' : 'prop-none';
  }
  return '';
}

// Build a <tr> for a product, keyed by its id so search reconciliation can
// find and reuse it. The rendered cell text is cached on the element. When
// logged in, every cell is an editable cell (pencil on hover) like the admin
// page's tables.
function buildRow(p) {
  const tr = document.createElement('tr');
  tr.dataset.id = p.id;
  tr.__cells = productCells(p);
  const listingsIdx = 3 + propertyColumns.length;
  for (let i = 0; i < tr.__cells.length; i++) {
    let td;
    if (i === listingsIdx) {
      td = buildListingsCell(p);
    } else if (authenticated) {
      td = editableProductCell(p, i, tr.__cells[i]);
    } else {
      td = document.createElement('td');
      td.textContent = tr.__cells[i];
    }
    if (i >= 3 && i < listingsIdx) {
      for (const cls of propertyCellClass(tr.__cells[i], propertyColumns[i - 3]).split(' ')) {
        if (cls) td.classList.add(cls);
      }
      applyProvenance(td, p, propertyColumns[i - 3]);
    }
    tr.appendChild(td);
  }
  if (authenticated) {
    tr.appendChild(buildRowActions(p));
  }
  // Cache the record so the pending poller can refresh this row in place,
  // and render a spinner when an agent run for the product is in flight.
  loadedProducts.set(String(p.id), p);
  if (pendingIds.has(String(p.id))) updateRowPending(tr, p);
  return tr;
}

// Stash a property cell's provenance as data attributes so the delegated
// tooltip (see initProvenanceTooltip) can show the source URL and confidence
// on hover without re-fetching anything.
function applyProvenance(td, p, c) {
  const sources = p.sources || {};
  const confidences = p.confidences || {};
  td.dataset.src = sources[c.id] || '';
  td.dataset.conf = confidences[c.id] !== undefined && confidences[c.id] !== null
    ? String(confidences[c.id]) : '';
  // Flag the failed-lookup and not-applicable cells so the tooltip can
  // explain the markers instead of the value provenance, and carry the AI's
  // own explanation (when the agent recorded one) for the tooltip.
  const status = p.statuses && p.statuses[c.id];
  td.dataset.na = status === 'not_applicable' ? '1' : '';
  td.dataset.attempted = (!(p.properties && (c.id in p.properties)) && p.attempts && p.attempts[c.id])
    ? '1' : '';
  td.dataset.note = (p.notes && typeof p.notes[c.id] === 'string') ? p.notes[c.id] : '';
}

// --- Inline cell editing (logged-in users) -----------------------------------
// Mirrors the admin page's editable cells: a hover-revealed pencil swaps the
// cell into edit mode; Enter or the floppy icon commits, Escape and blur
// cancel. Errors surface through the shared status line and keep the editor
// open.

// One editable cell. display is the rendered text, value the editor's initial
// value, onCommit the async save (receives the trimmed new value, throws on
// failure), buildEditor an optional custom editor factory (e.g. a select), and
// onDelete an optional async removal (shows a trash icon; throws on failure).
function editableCell(display, value, onCommit, buildEditor, onDelete) {
  const td = document.createElement('td');
  td.className = 'editable-cell';
  td.title = t('admin.edit.hint');

  const makeEditor = buildEditor || ((v) => {
    const input = document.createElement('input');
    input.type = 'text';
    input.value = v;
    return input;
  });

  const showDisplay = () => {
    td.textContent = '';
    const label = document.createElement('span');
    label.className = 'cell-text';
    label.textContent = display;
    const pencil = document.createElement('button');
    pencil.type = 'button';
    pencil.className = 'cell-icon cell-edit';
    pencil.textContent = '\u270E';
    pencil.setAttribute('aria-label', t('admin.edit.edit_aria'));
    pencil.addEventListener('click', (event) => {
      event.stopPropagation();
      startEdit();
    });
    td.append(label, pencil);
    if (onDelete) {
      const trash = document.createElement('button');
      trash.type = 'button';
      trash.className = 'cell-icon cell-delete';
      trash.textContent = '\uD83D\uDDD1';
      trash.setAttribute('aria-label', t('admin.edit.delete_aria'));
      trash.addEventListener('pointerdown', (event) => event.preventDefault());
      trash.addEventListener('click', async (event) => {
        event.stopPropagation();
        if (!window.confirm(t('confirm.remove_property_value'))) return;
        try {
          await Promise.resolve(onDelete());
        } catch (err) {
          setStatus(t('admin.error.save', { message: err.message }), true);
        }
      });
      td.append(trash);
    }
  };

  const showEditor = (current) => {
    const width = td.clientWidth;
    td.textContent = '';
    const editor = makeEditor(current);
    editor.className = 'cell-editor';
    const save = document.createElement('button');
    save.type = 'button';
    save.className = 'cell-icon cell-save';
    save.textContent = '\uD83D\uDCBE';
    save.setAttribute('aria-label', t('admin.edit.save_aria'));
    // Keep focus in the editor so the blur-cancel below does not fire
    // before the save click lands.
    save.addEventListener('pointerdown', (event) => event.preventDefault());
    save.addEventListener('click', (event) => {
      event.stopPropagation();
      commit(editor);
    });
    td.append(editor, save);
    // Match the editor to the space the cell content occupied (minus the
    // save button) so the columns do not jump when a cell enters edit mode.
    editor.style.width = Math.max(width - save.offsetWidth - 8, 60) + 'px';
    editor.focus();

    editor.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        commit(editor);
      } else if (event.key === 'Escape') {
        event.preventDefault();
        showDisplay();
      }
      event.stopPropagation();
    });
    editor.addEventListener('click', (event) => event.stopPropagation());
    editor.addEventListener('blur', () => showDisplay());
  };

  const commit = async (editor) => {
    const next = editor.value.trim();
    if (next === value) {
      showDisplay();
      return;
    }
    try {
      await Promise.resolve(onCommit(next));
      value = next;
      display = next;
      showDisplay();
    } catch (err) {
      setStatus(t('admin.error.save', { message: err.message }), true);
      editor.focus();
    }
  };

  const startEdit = () => showEditor(value);
  showDisplay();
  return td;
}

// The editable cell for column i of a product row. Base columns edit the
// product record (PUT /api/products/{id}); property columns edit (or clear)
// the product's value for that property.
function editableProductCell(p, i, cellText) {
  if (i === 0) {
    return editableCell(cellText, p.brand || '', (next) => commitProductField(p, 'brand', next));
  }
  if (i === 1) {
    return editableCell(cellText, p.model || '', (next) => commitProductField(p, 'model', next));
  }
  if (i === 2) {
    const currentId = String(typeIdByTitle(p.type) ?? '');
    return editableCell(cellText, currentId, (next) => commitProductType(p, next), () => {
      const select = document.createElement('select');
      const none = document.createElement('option');
      none.value = '';
      none.textContent = '\u2014';
      select.append(none);
      for (const tt of productTypes) {
        const opt = document.createElement('option');
        opt.value = String(tt.id);
        opt.textContent = tt.title;
        opt.selected = String(tt.id) === currentId;
        select.append(opt);
      }
      return select;
    });
  }
  const c = propertyColumns[i - 3];
  const has = Boolean(p.properties && (c.id in p.properties));
  const current = has ? String(p.properties[c.id]) : '';
  return editableCell(cellText, current, (next) => commitPropertyValue(p, c, next),
    () => {
      if (c.property_type === 'boolean') {
        const select = document.createElement('select');
        for (const [value, label] of [['', '\u2014'], ['true', t('value.yes')], ['false', t('value.no')]]) {
          const opt = document.createElement('option');
          opt.value = value;
          opt.textContent = label;
          opt.selected = value === current;
          select.append(opt);
        }
        return select;
      }
      const input = document.createElement('input');
      input.type = 'text';
      input.value = current;
      return input;
    },
    has ? () => removePropertyValue(p, c) : undefined);
}

// Remove a property value from a product via the trash button: the server-side
// deletion is shared with the empty-value commit path, and the row is rebuilt
// so cell classes and provenance refresh.
async function removePropertyValue(p, c) {
  await deletePropertyValue(p, c);
  delete p.properties[c.id];
  if (p.sources) delete p.sources[c.id];
  if (p.confidences) delete p.confidences[c.id];
  if (p.attempts) delete p.attempts[c.id];
  if (p.notes) delete p.notes[c.id];
  if (p.statuses) delete p.statuses[c.id];
  replaceRow(p);
}

// The product type id whose title matches, '' for an empty title, or null
// when no type carries that title (so a save never silently clears it).
function typeIdByTitle(title) {
  if (!title) return '';
  const hit = productTypes.find((tt) => tt.title === title);
  return hit ? String(hit.id) : null;
}

// Save a brand or model change: PUT the whole product record, keeping the
// current type by its id.
async function commitProductField(p, key, next) {
  const typeId = typeIdByTitle(p.type);
  if (typeId === null) throw new Error(t('error.unknown_type'));
  const body = { brand: p.brand || '', model: p.model || '', product_type: typeId };
  body[key] = next;
  const res = await fetch('/api/products/' + encodeURIComponent(p.id), {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`server returned ${res.status}`);
  p[key] = next;
  replaceRow(p);
}

// Save a type change from the select editor (the editor value is the type id).
async function commitProductType(p, id) {
  const tt = productTypes.find((x) => String(x.id) === id);
  const res = await fetch('/api/products/' + encodeURIComponent(p.id), {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ brand: p.brand || '', model: p.model || '', product_type: id }),
  });
  if (!res.ok) throw new Error(`server returned ${res.status}`);
  p.type = tt ? tt.title : '';
  replaceRow(p);
}

// Save (or clear) a product's value for one property. A non-empty value
// upserts via POST /api/property-values; an empty value removes the row. A
// human write replaces the value, so the cached provenance (source,
// confidence) and any failed-lookup marker are dropped locally too.
async function commitPropertyValue(p, c, next) {
  if (c.property_type === 'number' && next !== '' && !/^-?\d+(\.\d+)?$/.test(next)) {
    throw new Error(t('error.invalid_number'));
  }
  if (next === '') {
    if (p.properties && (c.id in p.properties)) {
      await deletePropertyValue(p, c);
      delete p.properties[c.id];
    }
  } else {
    const res = await fetch('/api/property-values', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ product_id: p.id, property_id: c.id, value: next }),
    });
    if (!res.ok) throw new Error(`server returned ${res.status}`);
    p.properties = p.properties || {};
    p.properties[c.id] = next;
  }
  if (p.sources) delete p.sources[c.id];
  if (p.confidences) delete p.confidences[c.id];
  if (p.attempts) delete p.attempts[c.id];
  if (p.notes) delete p.notes[c.id];
  if (p.statuses) delete p.statuses[c.id];
  replaceRow(p);
}

// Remove one product's value row for a property: the listing only carries the
// aggregated values, so the value's id is looked up via the per-product
// property-values endpoint first.
async function deletePropertyValue(p, c) {
  const res = await fetch('/api/property-values?product_id=' + encodeURIComponent(p.id));
  if (!res.ok) throw new Error(`server returned ${res.status}`);
  const data = await res.json();
  const row = (data.values || []).find((v) => v.property_id === c.id);
  if (!row) return;
  const del = await fetch('/api/property-values/' + encodeURIComponent(row.id), { method: 'DELETE' });
  if (!del.ok) throw new Error(`server returned ${del.status}`);
}

// Rebuild one product's row in place: after a successful edit or a merged
// refresh the row is rebuilt from the updated record, so the editable cells
// show the new value with fresh state.
function replaceRow(p) {
  const tr = tbody.querySelector(`tr[data-id="${CSS.escape(String(p.id))}"]`);
  if (tr) tr.replaceWith(buildRow(p));
}

// The hover-revealed action column for logged-in users, mirroring the admin
// page's cell-actions pattern: a trash bin that deletes the product, a
// sparkle that starts an agent run to fill the product's missing property
// values, and a log-list glyph that opens the product's enrichment run
// logs. All buttons are always laid out so nothing shifts on hover.
function buildRowActions(p) {
  const td = document.createElement('td');
  td.className = 'cell-actions';
  const name = `${p.brand} ${p.model}`;
  const complete = document.createElement('button');
  complete.type = 'button';
  complete.className = 'cell-icon cell-complete';
  // A four-pointed star instead of the \u2728 emoji: emoji render in full
  // color regardless of CSS, while this text glyph picks up var(--text).
  complete.textContent = '\u2726';
  complete.setAttribute('aria-label', t('row.complete_aria', { brand: p.brand, model: p.model }));
  complete.addEventListener('click', (event) => {
    event.stopPropagation();
    startCompletion(p, complete);
  });
  const logs = document.createElement('button');
  logs.type = 'button';
  logs.className = 'cell-icon cell-logs';
  // A list-glyph text glyph instead of an emoji, for the same reason as the
  // completion star above.
  logs.textContent = '\u2630';
  logs.setAttribute('aria-label', t('row.logs_aria', { brand: p.brand, model: p.model }));
  logs.addEventListener('click', (event) => {
    event.stopPropagation();
    openEnrichmentLogs(p);
  });
  const remove = document.createElement('button');
  remove.type = 'button';
  remove.className = 'cell-icon cell-remove';
  remove.textContent = '\uD83D\uDDD1';
  remove.setAttribute('aria-label', t('row.delete_aria', { brand: p.brand, model: p.model }));
  remove.addEventListener('click', (event) => {
    event.stopPropagation();
    deleteProduct(p);
  });
  td.append(complete, logs, remove);
  return td;
}

// Ask once, then delete the product and drop its row.
async function deleteProduct(p) {
  if (!window.confirm(t('confirm.delete_product', { brand: p.brand, model: p.model }))) return;
  try {
    const res = await fetch('/api/products/' + encodeURIComponent(p.id), { method: 'DELETE' });
    if (!res.ok) throw new Error(`server returned ${res.status}`);
    loadedProducts.delete(String(p.id));
    const tr = tbody.querySelector(`tr[data-id="${CSS.escape(String(p.id))}"]`);
    if (tr) tr.remove();
  } catch (err) {
    setStatus(t('error.load_products', { message: err.message }), true);
  }
}

// --- Enrichment run log dump (logged-in) -------------------------------------
// The row's list glyph opens a dialog with one big read-only textarea holding
// every recorded agent run of the product — run metadata, outcome, and the
// full conversation transcript (prompts, tool calls, tool results) — as plain
// text, so everything can be selected and copied in one go.

let logDialog = null;
let logTitle = null;
let logText = null;
let logClose = null;
// The product whose logs the dialog shows.
let logProduct = null;

// jsdom implements the <dialog> element but not showModal(); browsers do.
// The fallback keeps the UI tests (and any ancient browser) working.
function openDialogElement(dialog) {
  if (typeof dialog.showModal === 'function') dialog.showModal();
  else dialog.setAttribute('open', '');
}

// Mirror of openDialogElement: real browsers must close() so the dialog
// leaves the top layer (an `open = false` flip leaves it there, swallowing
// every pointer event and breaking the next showModal); jsdom has no
// close(), so the attribute flip is the only fallback there.
function closeDialogElement(dialog) {
  if (typeof dialog.close === 'function') dialog.close();
  else dialog.removeAttribute('open');
}

function ensureLogDialog() {
  if (logDialog) return;
  logDialog = document.getElementById('log-dialog');
  logTitle = document.getElementById('log-title');
  logText = document.getElementById('log-text');
  logClose = document.getElementById('log-close');
  logClose.textContent = t('logs.close');
  logClose.addEventListener('click', () => {
    if (logDialog) closeDialogElement(logDialog);
  });
}

// Plain text for one transcript message: its role, content, tool calls with
// their arguments, and the tool_call id that ties a result to its call.
function logMessageText(msg) {
  const lines = ['--- ' + (msg.role || 'unknown') + ' ---'];
  if (msg.role === 'tool' && msg.tool_call_id) lines.push('(tool_call_id ' + msg.tool_call_id + ')');
  const body = (msg.content && String(msg.content)) || '';
  if (body) lines.push(body);
  for (const call of msg.tool_calls || []) {
    let args = call.function?.arguments || '';
    try { args = JSON.stringify(JSON.parse(args), null, 2); } catch { /* keep raw */ }
    lines.push('[tool call ' + (call.function?.name || '?') + ']', args);
  }
  return lines.join('\n');
}

// Plain text for one run: a header line with its metadata and outcome, then
// the full conversation. detail (fetched separately) supplies the transcript;
// when it failed to load the summary alone still shows.
function logRunText(l, detail) {
  const bits = [
    new Date(l.started_at).toLocaleString(),
    l.model || t('logs.default_model'),
  ];
  if (l.duration_ms > 0) bits.push(formatDuration(l.duration_ms));
  bits.push(l.status === 'error' ? (l.error || t('logs.run_failed')) : l.status);
  const lines = ['=== ' + bits.join(' · ') + ' ==='];
  if (l.response) lines.push(l.response);
  for (const msg of (detail && detail.transcript) || []) {
    lines.push(logMessageText(msg));
  }
  return lines.join('\n');
}

// Load every run of the product — the summary list first, then all
// transcripts in parallel — and dump everything into the textarea, newest
// run first like the server orders it.
function showLogs() {
  ensureLogDialog();
  logTitle.textContent = t('logs.title', { brand: logProduct.brand, model: logProduct.model });
  logText.setAttribute('aria-label', logTitle.textContent);
  logText.value = '';
  const base = '/api/products/' + encodeURIComponent(logProduct.id) + '/enrichment-logs';
  fetch(base)
    .then(async (res) => {
      if (!res.ok) throw new Error(`server returned ${res.status}`);
      return res.json();
    })
    .then(async (data) => {
      const logs = data.logs || [];
      if (logs.length === 0) {
        logText.value = t('logs.empty');
        return;
      }
      const details = await Promise.all(logs.map(async (l) => {
        try {
          const res = await fetch(base + '/' + encodeURIComponent(l.id));
          if (!res.ok) throw new Error(`server returned ${res.status}`);
          return await res.json();
        } catch (err) {
          // One unreadable transcript must not hide the other runs; the
          // summary metadata still lands in the dump.
          return null;
        }
      }));
      logText.value = logs.map((l, i) => logRunText(l, details[i])).join('\n\n');
    })
    .catch((err) => {
      logText.value = t('logs.load_failed', { message: err.message });
    });
  openDialogElement(logDialog);
}

// Open the dialog for one product. logProduct is set first: showLogs reads
// it.
function openEnrichmentLogs(p) {
  ensureLogDialog();
  logProduct = p;
  showLogs();
}

// "90 s", "3 m 24 s" — the run durations the log list shows next to each run.
function formatDuration(ms) {
  const s = Math.round(ms / 1000);
  if (s < 90) return s + ' s';
  return Math.floor(s / 60) + ' m ' + (s % 60) + ' s';
}

// True when a property has no value yet, or has a value but no provenance
// (an empty source_url).
function isIncomplete(p, c) {
  const props = p.properties || {};
  if (!(c.id in props)) return true;
  const sources = p.sources || {};
  return !sources[c.id];
}

// The properties of the category the product has no value for yet, or that
// have a value but no provenance (an empty source_url). The agent gets these
// (and the whole product record) in its prompt, so it never needs to look the
// product up itself.
function missingProperties(p) {
  return propertyColumns
    .filter((c) => isIncomplete(p, c))
    .map((c) => ({ id: c.id, name: c.name, property_type: c.property_type }));
}

// Start an agent run for one product: POST the product's full record (plus
// the missing property definitions) to /api/llm/complete. The server answers
// immediately (202), runs the agent in the background, and tracks the run in
// its pending registry; the row shows spinners on its missing property cells
// and the pending poller below refreshes the row the moment the run finishes.
// Poll interval for the pending registry. The poll override exists for the
// UI tests; production always uses the default.
const AGENT_POLL_MS = Number(window.AGENT_POLL_MS) || 10000;

// Product ids whose background agent run is still in flight, mirrored from
// the server's GET /api/llm/pending. The state lives on the server, so a
// page reload restores the spinners and the finished values without a
// manual refresh. The set drives updateRowPending.
const pendingIds = new Set();
// Loaded products by id, so the pending poller can refresh a finished row
// from its cached record without waiting for a full listing reload.
const loadedProducts = new Map();

// Snapshot the pending registry: mark the loaded rows that are already
// running (e.g. a reload while an agent run started earlier) and render
// their spinners right away.
async function fetchPending() {
  try {
    const res = await fetch('/api/llm/pending');
    if (!res.ok) return;
    const data = await res.json();
    for (const id of data.pending || []) {
      pendingIds.add(String(id));
    }
    for (const [id, p] of loadedProducts) {
      if (!pendingIds.has(id)) continue;
      const tr = tbody.querySelector(`tr[data-id="${CSS.escape(id)}"]`);
      if (tr) updateRowPending(tr, p);
    }
  } catch (err) {
    /* unreachable server: the next poll retries */
  }
}

// One tick of the pending poller. Runs that disappeared from the server's
// registry have finished: refresh their rows in place from the (now
// updated) product record. Runs that appeared are started elsewhere (e.g.
// another tab): render their spinners too.
async function pollPending() {
  let pending;
  try {
    const res = await fetch('/api/llm/pending');
    if (!res.ok) return;
    const data = await res.json();
    pending = new Set((data.pending || []).map(String));
  } catch (err) {
    return; // unreachable server: the next tick retries
  }
  for (const id of [...pendingIds]) {
    if (pending.has(id)) continue;
    pendingIds.delete(id);
    await refreshFinishedRow(id);
  }
  for (const id of pending) {
    if (pendingIds.has(id)) continue;
    pendingIds.add(id);
    const p = loadedProducts.get(id);
    if (!p) continue;
    const tr = tbody.querySelector(`tr[data-id="${CSS.escape(id)}"]`);
    if (tr) updateRowPending(tr, p);
  }
  // Count consecutive idle ticks (nothing pending here and on the server):
  // a few in a row stop the poll loop until the next run starts.
  if (pendingIds.size === 0 && pending.size === 0) pollIdleTicks++;
  else pollIdleTicks = 0;
}

// Refresh one finished run's row: merge the product's fresh record into the
// cached one, redraw the cells, and restore the complete button.
async function refreshFinishedRow(id) {
  const p = loadedProducts.get(id);
  if (!p) return;
  try {
    const res = await fetch('/api/products/' + encodeURIComponent(id));
    if (res.ok) {
      const fresh = await res.json();
      p.properties = Object.assign({}, p.properties, fresh.properties || {});
      p.sources = Object.assign({}, p.sources, fresh.sources || {});
      p.confidences = Object.assign({}, p.confidences, fresh.confidences || {});
      p.attempts = Object.assign({}, p.attempts, fresh.attempts || {});
      // Notes must be replaced, not merged: a retried run clears the note
      // (recordEnrichmentAttempts sets note = NULL), and merging would keep
      // the stale explanation showing next to the fresh attempt marker.
      p.notes = fresh.notes || {};
      p.statuses = fresh.statuses || {};
    }
  } catch (err) {
    /* keep the stale record; a later listing reload corrects it */
  }
  refreshRow(p);
  const tr = tbody.querySelector(`tr[data-id="${CSS.escape(id)}"]`);
  if (tr) updateRowPending(tr, p);
  setStatus(t('status.agent_done'));
}

// Keep polling the pending registry while there is something to track: any
// run in flight (locally started or restored from the server after a reload)
// keeps the loop alive; a few consecutive idle ticks (nothing pending here
// and on the server) stop it, and the next started run restarts it. This
// keeps the page from polling forever when nothing is happening.
let pollTimer = null;
let pollIdleTicks = 0;
function pollPendingLoop() {
  if (pollTimer !== null) return; // already running
  const tick = () => {
    pollTimer = null;
    pollPending().finally(() => {
      if (pollIdleTicks >= 3) return; // idle: stop until the next run
      pollTimer = setTimeout(tick, AGENT_POLL_MS);
    });
  };
  pollTimer = setTimeout(tick, 0);
}

async function startCompletion(p, button) {
  if (button.disabled) return;
  button.disabled = true;
  button.textContent = '\u23F3';
  setStatus(t('status.agent_started'));
  try {
    const res = await fetch('/api/llm/complete', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        product: {
          id: p.id, brand: p.brand, model: p.model, type: p.type,
          properties: p.properties || {},
        },
        missing: missingProperties(p),
      }),
    });
    if (!res.ok) throw new Error(`server returned ${res.status}`);
  } catch (err) {
    setStatus(t('status.agent_failed', { message: err.message }), true);
    button.disabled = false;
    button.textContent = '\u2726';
    return;
  }
  // The run is tracked server-side: register it locally so the row shows
  // its spinners now, and (re)start the pending poller so it notices the
  // finish — even if the loop had gone idle.
  pendingIds.add(String(p.id));
  loadedProducts.set(String(p.id), p);
  pollIdleTicks = 0;
  pollPendingLoop();
  const tr = tbody.querySelector(`tr[data-id="${CSS.escape(String(p.id))}"]`);
  if (tr) updateRowPending(tr, p);
}

// Redraw one row's cells in place (same logic as the search reconciliation).
function refreshRow(p) {
  const tr = tbody.querySelector(`tr[data-id="${CSS.escape(String(p.id))}"]`);
  if (!tr) return;
  // Editable rows are rebuilt from the updated record: patching the text of
  // their cells in place would destroy the pencil/editor markup.
  if (authenticated) {
    tr.replaceWith(buildRow(p));
    return;
  }
  const cells = productCells(p);
  const tds = tr.querySelectorAll('td');
  const listingsIdx = 3 + propertyColumns.length;
  for (let i = 0; i < cells.length; i++) {
    if (i === listingsIdx) {
      if (tds[i] && tds[i].textContent !== cells[i]) {
        tds[i].replaceWith(buildListingsCell(p));
      }
      continue;
    }
    if (tds[i] && tds[i].textContent !== cells[i]) {
      tds[i].textContent = cells[i];
      if (i >= 3) {
        tds[i].className = propertyCellClass(cells[i], propertyColumns[i - 3]);
      }
    }
    if (i >= 3 && tds[i]) {
      applyProvenance(tds[i], p, propertyColumns[i - 3]);
    }
  }
  tr.__cells = cells;
  updateRowPending(tr, p);
}

// Toggle one row's pending state: while the product has an agent run in
// flight, every missing property cell shows a spinning circle instead of the
// '-' placeholder and the complete button waits; once the run finishes the
// spinner is removed and the refreshed values show. The complete button
// tracks the same state so a page reload cannot double-start a run.
function updateRowPending(tr, p) {
  const pending = pendingIds.has(String(p.id));
  const tds = tr.querySelectorAll('td');
  propertyColumns.forEach((c, i) => {
    const td = tds[i + 3];
    if (!td) return;
    const spin = pending && isIncomplete(p, c);
    if (spin) {
      if (!td.classList.contains('prop-pending')) {
        td.classList.add('prop-pending');
        const s = document.createElement('span');
        s.className = 'prop-spinner';
        // Anchor the animation phase to a shared clock so spinners that
        // appear at different times all rotate in sync with each other.
        s.style.animationDelay = `-${Math.round(performance.now() % 800)}ms`;
        s.setAttribute('aria-label', t('prop.pending_aria'));
        // In editable cells the spinner takes the value text's spot: at the
        // content position, left of the pencil icon like every other value.
        const text = td.querySelector('.cell-text');
        if (text) td.insertBefore(s, text); else td.appendChild(s);
      }
    } else {
      td.classList.remove('prop-pending');
      const s = td.querySelector('.prop-spinner');
      if (s) s.remove();
    }
  });
  const btn = tr.querySelector('button.cell-complete');
  if (btn) {
    btn.disabled = pending;
    btn.textContent = pending ? '\u23F3' : '\u2726';
  }
}

function addRow(p) {
  tbody.appendChild(buildRow(p));
}

// Layout-shift prevention: fill the table with placeholder rows before the
// first data fetch resolves. The table then occupies (roughly) its loaded
// height from first paint instead of collapsing to the header and pushing
// everything below it down when rows arrive. Rows carry the skeleton-row
// class and no dataset.id, so renderSearchResults drops them automatically;
// loadPage removes them right before real rows are appended. Row heights
// match real rows (same cell padding), widths are randomized short bars so
// the pattern reads as loading, not data.
function renderSkeleton(n) {
  for (let i = 0; i < n; i++) {
    const tr = document.createElement('tr');
    tr.className = 'skeleton-row';
    tr.setAttribute('aria-hidden', 'true');
    const cols = currentColumns().length + (authenticated ? 1 : 0);
    for (let c = 0; c < cols; c++) {
      const td = document.createElement('td');
      const bar = document.createElement('span');
      bar.className = 'skeleton-bar';
      bar.style.width = `${45 + ((i * 7 + c * 13) % 40)}%`;
      td.appendChild(bar);
      tr.appendChild(td);
    }
    tbody.appendChild(tr);
  }
}

function clearSkeleton() {
  for (const tr of Array.from(tbody.children)) {
    if (tr.classList.contains('skeleton-row')) tr.remove();
  }
}

function skeletonCount() {
  let n = 0;
  for (const tr of tbody.children) {
    if (!tr.classList.contains('skeleton-row')) n++;
  }
  return n;
}

// Replace the tbody contents with the search results, reusing any existing row
// whose id and cell text already match. Rows that didn't change are preserved
// (so FTS with identical results causes no redraw); only added, removed, and
// changed rows are updated. Rows are reordered to keep the server's sort order.
function renderSearchResults(products) {
  const wanted = new Set(products.map(p => p.id));
  for (const tr of Array.from(tbody.children)) {
    if (!wanted.has(tr.dataset.id)) tr.remove();
  }

  const existing = {};
  for (const tr of Array.from(tbody.children)) {
    if (tr.dataset.id) existing[tr.dataset.id] = tr;
  }

  let index = 0;
  for (const p of products) {
    const cells = productCells(p);
    let tr = existing[p.id];
    if (tr) {
      if (tr.__cells && tr.__cells.join('\u0000') !== cells.join('\u0000')) {
        if (authenticated) {
          // Editable rows are rebuilt instead of patched so the pencil and
          // editor markup survive a reconciliation.
          tr.replaceWith(buildRow(p));
          if (pendingIds.has(String(p.id))) updateRowPending(tr, p);
          index++;
          continue;
        }
        const tds = tr.querySelectorAll('td');
        const listingsIdx = 3 + propertyColumns.length;
        for (let i = 0; i < cells.length; i++) {
        if (i === listingsIdx) {
          if (tds[i] && tds[i].textContent !== cells[i]) {
            tds[i].replaceWith(buildListingsCell(p));
          }
          continue;
        }
        if (tds[i] && tds[i].textContent !== cells[i]) {
          tds[i].textContent = cells[i];
          if (i >= 3) {
            tds[i].className = propertyCellClass(cells[i], propertyColumns[i - 3]);
          }
        }
          if (i >= 3 && tds[i]) {
            applyProvenance(tds[i], p, propertyColumns[i - 3]);
          }
        }
        tr.__cells = cells;
      }
    } else {
      tr = buildRow(p);
    }
    if (tbody.children[index] !== tr) {
      tbody.insertBefore(tr, tbody.children[index] || null);
    }
    if (pendingIds.has(String(p.id))) updateRowPending(tr, p);
    index++;
  }
}

function setStatus(text, isError) {
  status.textContent = text;
  status.className = isError ? 'error' : '';
}

// The row count lives in the toolbar above the table (#match-status in
// index.html), separate from the #status line used for load and error
// messages. The number and the label each sit in a span, so an unchanged
// count or label provokes no redraw.
const matchStatus = document.getElementById('match-status');
const matchCountSpan = document.getElementById('match-count');
const matchLabel = document.getElementById('match-label');

// Property filter UI elements.
const addFilterBtn = document.getElementById('add-filter');
const filterBuilder = document.getElementById('filter-builder');
const filterProperty = document.getElementById('filter-property');
const filterOperator = document.getElementById('filter-operator');
const filterValueHost = document.getElementById('filter-value-host');
const filterApply = document.getElementById('filter-apply');
const filterCancel = document.getElementById('filter-cancel');
const activeFiltersHost = document.getElementById('active-filters');

function showCount(total, label) {
  if (matchCountSpan.textContent !== String(total)) {
    matchCountSpan.textContent = total;
  }
  if (matchLabel.textContent !== label) {
    matchLabel.textContent = label;
  }
  matchStatus.classList.add('is-ready');
}

async function loadPage() {
  if (loading || !hasMore) return;
  loading = true;
  const gen = loadGen;
  // Consume the sort-reload marker: this request re-fetches page 1 of the new
  // order and must reconcile the rows instead of appending; later scroll
  // pages append again.
  const reconcile = sortReload;
  sortReload = false;
  // During any search-mode load (full-text or property-filtered), leave the
  // status line alone so the previous match count stays visible until the
  // new results arrive.
  const searchMode = currentQuery !== '' || activeFilters.length > 0;
  if (!searchMode) setStatus(t('status.loading'));
  try {
    const query = new URLSearchParams({ limit: searchMode ? SEARCH_LIMIT : PAGE_SIZE });
    if (currentQuery) {
      query.set('q', currentQuery);
    }
    if (activeFilters.length > 0) {
      query.set('property_filters', propertyFiltersParam());
    }
    if (currentTypeId) {
      query.set('product_type_id', currentTypeId);
    }
    if (sortKey) {
      query.set('sort', sortKey);
      query.set('dir', sortDir);
    }
    // Keyset cursors only make sense in the default order; a sorted listing
    // pages by plain offset (rows are appended, so the loaded count is the
    // offset into the sorted sequence). A sort reload restarts the sequence,
    // so it omits the offset and fetches page 1.
    const keyset = !sortKey || (sortKey === 'brand' && sortDir === 'asc');
    // Append pages already know the total shown with page 1; the count only
    // changes when the filter changes, which starts a fresh page 1. Skip the
    // server's extra COUNT query for them.
    // Skeleton placeholders occupy the table while this page is in flight;
    // row counts must only ever reflect real data rows.
    const realRowCount = skeletonCount();
    const skipTotal = !searchMode && !reconcile &&
      (cursor !== null || realRowCount > 0);
    if (!searchMode && keyset && cursor !== null) {
      query.set('after_brand', cursor.brand);
      query.set('after_model', cursor.model);
      query.set('after_id', cursor.id);
    } else if (!searchMode && !reconcile) {
      query.set('offset', String(realRowCount));
    }
    if (skipTotal) {
      query.set('total', '0');
    }
    const res = await fetch(`/api/products?${query}`);
    if (!res.ok) {
      throw new Error(`server returned ${res.status}`);
    }
    const page = await res.json();
    if (gen !== loadGen) return; // superseded by a newer reload
    let last = null;
    if (searchMode || reconcile) {
      renderSearchResults(page.products);
    } else {
      // Page 1 of a fresh listing replaces the reserved skeleton rows in the
      // same frame as the real rows, so the table never collapses in between.
      clearSkeleton();
      for (const p of page.products) {
        addRow(p);
      }
    }
    if (!searchMode && page.products.length) {
      last = page.products[page.products.length - 1];
    }
    cursor = last ? { brand: last.brand, model: last.model, id: last.id } : cursor;
    hasMore = page.hasMore;
    loading = false;
    if (searchMode) {
      const total = page.total || page.products.length;
      showCount(total, tCount('count.matches', total));
      // Scroll loading is off in search mode, so a status line left over
      // from the full listing ("Scroll to load more…") must not survive:
      // an empty result states so explicitly, otherwise the line is
      // cleared (the count lives above the table).
      setStatus(page.products.length === 0 ? t('status.no_matches') : '');
    } else {
      // The listing reports the total matching the category filter, even
      // though only one page is loaded so far. Append pages omit the total
      // (the count is already on screen and unchanged) and keep it.
      if (page.total !== null && page.total !== undefined) {
        showCount(page.total ?? 0, currentTypeId
          ? tCount('count.products_in_category', page.total ?? 0)
          : tCount('count.products', page.total ?? 0));
      }
      setStatus(hasMore ? t('status.scroll_more') : t('status.all_loaded'));
      fillViewport();
    }
  } catch (err) {
    if (gen !== loadGen) return; // a newer reload owns the request state
    loading = false;
    // The reserved skeleton rows must not outlive a failed page fetch:
    // they would sit in the table forever with the error status above it.
    clearSkeleton();
    setStatus(t('error.load_products', { message: err.message }), true);
  }
}

// True when the document cannot scroll because the whole listing fits in
// the viewport. In that state a scroll event can never fire, so the
// "scroll to load more" hint would be a dead end.
function documentScrollable() {
  return document.documentElement.scrollHeight > window.innerHeight;
}

// After a listing page, when more pages remain but the document still
// cannot scroll (a large browser window showing every loaded row), load
// the next page right away instead of waiting for a scroll that cannot
// happen. Search mode renders one reconciled page and never pages.
function fillViewport() {
  if (hasMore && !documentScrollable()) loadPage();
}

function onScroll() {
  // No infinite scroll while a search or property filters narrow the list:
  // those results render as one reconciled page.
  if (currentQuery !== '' || activeFilters.length > 0) return;
  const doc = document.documentElement;
  const scrollY = window.scrollY || window.pageYOffset || doc.scrollTop;
  const nearBottom = window.innerHeight + scrollY >= doc.scrollHeight - 200;
  if (nearBottom) {
    loadPage();
  }
}

async function loadTypes() {
  try {
    const res = await fetch('/api/product-types');
    if (!res.ok) {
      throw new Error(`server returned ${res.status}`);
    }
    const data = await res.json();
    productTypes = data.types || [];
    // A server-rendered document already carries the category <option>s
    // (internal/api/home.go), so its width is final at first paint; only the
    // static fallback path fills the select here.
    if (categorySelect.options.length <= 1) {
      for (const t of productTypes) {
        const opt = document.createElement('option');
        opt.value = t.id;
        opt.textContent = t.title;
        categorySelect.appendChild(opt);
      }
    }
    // The first load reads ?category before any option exists (select.value
    // assignment is a no-op then), so re-sync once the options are in.
    if (currentTypeId && productTypes.some((t) => t.id === currentTypeId)) {
      categorySelect.value = currentTypeId;
    }
  } catch (err) {
    setStatus(t('error.load_categories', { message: err.message }), true);
  }
}

function resetAndReload() {
  tbody.textContent = '';
  renderSkeleton(PAGE_SIZE);
  cursor = null;
  hasMore = true;
  loading = false;
  loadGen++;
  loadPage();
}

// --- Property filters -------------------------------------------------------

function propertyById(id) {
  return propertyColumns.find((c) => c.id === id) || null;
}

// A raw URL value fits the property when it matches the property's declared
// type: numbers must be integer or decimal literals, booleans must be
// true/false (case-insensitive), text accepts anything non-empty.
function validRawValue(prop, raw) {
  if (!prop || raw === '') return false;
  if (prop.property_type === 'number') return /^-?\d+(\.\d+)?$/.test(raw);
  if (prop.property_type === 'boolean') return /^(true|false)$/i.test(raw);
  return true;
}

// Turn a raw URL value into the JSON type the property_filters API expects:
// numbers as numbers, booleans as booleans, everything else as text.
function typedValue(prop, raw) {
  if (prop && prop.property_type === 'number') return Number(raw);
  if (prop && prop.property_type === 'boolean') return raw.toLowerCase() === 'true';
  return raw;
}

// Canonical string form of a filter list, for state-vs-URL comparisons.
function filtersKey(filters) {
  return filters.map((f) => `${f.id}\u0000${f.op}\u0000${f.raw}`).join('\u0001');
}

// Read the filter params out of a URLSearchParams set, keeping only
// syntactically valid entries: a value filter is filter=<id>:<value> (the id
// is a UUID, so the first ':' splits it even when the value contains
// colons), "is set" is has=<id>, "is unset" is missing=<id>. Property-level
// validation (does the category have this property, does the value fit its
// type) happens in sanitizeFilters once the columns are known.
function readFilterParams(params) {
  const out = [];
  for (const entry of params.getAll('filter')) {
    const sep = entry.indexOf(':');
    if (sep <= 0) continue;
    const raw = entry.slice(sep + 1);
    if (raw === '') continue;
    out.push({ id: entry.slice(0, sep), op: 'eq', raw });
  }
  for (const id of params.getAll('has')) {
    if (id) out.push({ id, op: 'set', raw: '' });
  }
  for (const id of params.getAll('missing')) {
    if (id) out.push({ id, op: 'unset', raw: '' });
  }
  // Drop exact duplicates so a hand-edited URL cannot double a chip.
  const seen = new Set();
  return out.filter((f) => {
    const key = `${f.id}\u0000${f.op}\u0000${f.raw}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

// Drop filters whose property the current columns do not contain, and value
// filters whose raw value does not fit the property's type. Returns true
// when something was dropped, so the caller repairs the address bar.
function sanitizeFilters() {
  const before = activeFilters.length;
  activeFilters = activeFilters.filter((f) => {
    const prop = propertyById(f.id);
    return prop !== null && (f.op !== 'eq' || validRawValue(prop, f.raw));
  });
  return activeFilters.length !== before;
}

// Write the active filters into the URL: a value filter is
// filter=<property id>:<value>, "is set" is has=<id>, "is unset" is
// missing=<id>; repeated params carry multiple filters. The id is a UUID, so
// the first ':' splits it from the value even when the value contains
// colons. The filter pairs are appended by hand (a raw ':' is legal in a
// query string) so the address bar stays readable — URLSearchParams would
// percent-encode it to %3A. replace=true rewrites the current history entry
// (URL repair); otherwise a new entry is pushed so the change stays
// reachable via back/forward.
function writeFilterParams(replace) {
  const params = new URLSearchParams(window.location.search);
  params.delete('filter');
  params.delete('has');
  params.delete('missing');
  const parts = [];
  for (const [key, value] of params) {
    parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
  }
  for (const f of activeFilters) {
    if (f.op === 'eq') parts.push(`filter=${f.id}:${encodeURIComponent(f.raw)}`);
    else if (f.op === 'set') parts.push(`has=${f.id}`);
    else parts.push(`missing=${f.id}`);
  }
  const url = new URL(window.location.href);
  url.search = parts.join('&');
  if (replace) {
    history.replaceState({}, '', url);
  } else {
    history.pushState({}, '', url);
  }
}

// The property_filters API clauses for the active filters, in order. The
// server ANDs every clause. Clause names are property NAMES because the
// search index maps the dynamic properties object by name.
function propertyFiltersParam() {
  return JSON.stringify(activeFilters.map((f) => {
    const prop = propertyById(f.id);
    const clause = { name: prop ? prop.name : f.id };
    if (f.op === 'set') {
      clause.has = true;
    } else if (f.op === 'unset') {
      clause.missing = true;
    } else {
      clause.value = typedValue(prop, f.raw);
    }
    return clause;
  }));
}

// The filter UI follows progressive disclosure: a single "+ Filter" button
// per category opens the builder, and applied filters show as removable
// chips, so a type with ten properties still renders zero filter inputs
// until the user asks for one.
function renderFilterControls() {
  // Reserve the button's slot instead of display-toggling it (see the CSS):
  // showing it after the properties fetch must not re-wrap the toolbar.
  addFilterBtn.classList.toggle('is-ready',
    Boolean(currentTypeId) && propertyColumns.length > 0);
  addFilterBtn.setAttribute('aria-expanded', filterBuilder.hidden ? 'false' : 'true');

  activeFiltersHost.textContent = '';
  for (const f of activeFilters) {
    activeFiltersHost.appendChild(buildFilterChip(f));
  }
  activeFiltersHost.hidden = activeFilters.length === 0;
}

function buildFilterChip(f) {
  const prop = propertyById(f.id);
  const chip = document.createElement('span');
  chip.className = 'filter-chip';
  // The label is a button that re-opens the builder pre-filled with this
  // filter, so an applied filter can be edited instead of only removed.
  const edit = document.createElement('button');
  edit.type = 'button';
  edit.className = 'chip-edit';
  edit.textContent = chipLabel(f, prop);
  edit.setAttribute('aria-label', t('chip.edit_aria', { name: prop ? prop.name : f.id }));
  edit.addEventListener('click', () => editFilter(f));
  const remove = document.createElement('button');
  remove.type = 'button';
  remove.className = 'chip-remove';
  remove.textContent = '\u00D7';
  remove.setAttribute('aria-label', t('chip.remove_aria', { name: prop ? prop.name : f.id }));
  remove.addEventListener('click', () => removeFilter(f));
  chip.append(edit, remove);
  return chip;
}

function chipLabel(f, prop) {
  const name = prop ? prop.name : f.id;
  if (f.op === 'set') return t('chip.is_set', { name });
  if (f.op === 'unset') return t('chip.is_unset', { name });
  if (prop && prop.property_type === 'boolean') {
    return `${name} = ${f.raw.toLowerCase() === 'true' ? t('value.yes') : t('value.no')}`;
  }
  return `${name} = ${f.raw}`;
}

function removeFilter(f) {
  activeFilters = activeFilters.filter((x) => x !== f);
  writeFilterParams(false);
  renderFilterControls();
  resetAndReload();
}

// Populate and reveal the builder. With no argument it starts empty for a
// new filter; with an existing filter it is pre-filled for editing (the
// commit button reads "Update" while editing).
function showBuilder(editing) {
  filterProperty.textContent = '';
  for (const c of propertyColumns) {
    const opt = document.createElement('option');
    opt.value = c.id;
    opt.textContent = c.name;
    filterProperty.appendChild(opt);
  }
  if (editing) {
    filterProperty.value = editing.id;
    filterOperator.value = editing.op;
    renderValueControl();
    const control = document.getElementById('filter-value');
    if (control) control.value = editing.raw;
    filterApply.textContent = t('filter.update');
  } else {
    if (propertyColumns.length > 0) filterProperty.value = propertyColumns[0].id;
    filterOperator.value = 'eq';
    renderValueControl();
    filterApply.textContent = t('filter.apply');
  }
  editingFilter = editing || null;
  updateApplyEnabled();
  filterBuilder.hidden = false;
  addFilterBtn.setAttribute('aria-expanded', 'true');
  filterProperty.focus();
}

function openBuilder() {
  showBuilder(null);
}

// Clicking a chip's label re-opens the builder pre-filled with that filter.
function editFilter(f) {
  if (!propertyById(f.id)) return;
  showBuilder(f);
}

function closeBuilder() {
  filterBuilder.hidden = true;
  addFilterBtn.setAttribute('aria-expanded', 'false');
  editingFilter = null;
  filterApply.textContent = t('filter.apply');
}

// The value control matches the property's declared type: a number input
// (decimals allowed) for numbers, a Yes/No select for booleans, plain text
// otherwise. The "is set"/"is unset" operators need no value at all.
function renderValueControl() {
  filterValueHost.textContent = '';
  const prop = propertyById(filterProperty.value);
  if (filterOperator.value !== 'eq' || !prop) {
    updateApplyEnabled();
    return;
  }
  let control;
  if (prop.property_type === 'number') {
    control = document.createElement('input');
    control.type = 'number';
    control.step = 'any';
  } else if (prop.property_type === 'boolean') {
    control = document.createElement('select');
    for (const [value, label] of [['true', t('value.yes')], ['false', t('value.no')]]) {
      const opt = document.createElement('option');
      opt.value = value;
      opt.textContent = label;
      control.appendChild(opt);
    }
  } else {
    control = document.createElement('input');
    control.type = 'text';
  }
  control.id = 'filter-value';
  control.addEventListener('input', updateApplyEnabled);
  filterValueHost.appendChild(control);
  updateApplyEnabled();
}

// Apply stays disabled until the value control holds something the
// property's type can accept, so an invalid filter can never be applied.
function updateApplyEnabled() {
  const prop = propertyById(filterProperty.value);
  const control = document.getElementById('filter-value');
  filterApply.disabled = filterOperator.value === 'eq' &&
    !(prop && control && validRawValue(prop, control.value.trim()));
}

function applyBuilder() {
  const prop = propertyById(filterProperty.value);
  if (!prop) {
    closeBuilder();
    return;
  }
  const f = { id: prop.id, op: filterOperator.value, raw: '' };
  if (f.op === 'eq') {
    const control = document.getElementById('filter-value');
    const raw = control ? control.value.trim() : '';
    if (!validRawValue(prop, raw)) return;
    f.raw = raw;
  }
  const editing = editingFilter;
  closeBuilder();
  // One filter per property: a second filter on the same property replaces
  // the first instead of AND-ing into an empty result. While editing, the
  // edited chip is replaced exactly — even when the property was switched —
  // so an edit never leaves the original filter behind.
  activeFilters = activeFilters.filter((x) => x !== editing && x.id !== prop.id);
  activeFilters.push(f);
  writeFilterParams(false);
  renderFilterControls();
  resetAndReload();
}

// Fetch and install the current category's property columns, then validate
// the URL-borne sort and filters against them. Uses currentTypeId directly
// so it can run during the parallel first load, before <option>s exist in
// the category select. Returns without rendering on fetch failure (the
// status line carries the error).
async function loadProperties() {
  propertyColumns = [];
  if (currentTypeId) {
    try {
      const res = await fetch(`/api/properties?product_type_id=${encodeURIComponent(currentTypeId)}`);
      if (!res.ok) {
        throw new Error(`server returned ${res.status}`);
      }
      const data = await res.json();
      propertyColumns = data.properties;
      booleanPropertyIds = new Set(data.properties.filter(p => p.property_type === 'boolean').map(p => p.id));
    } catch (err) {
      setStatus(t('error.load_properties', { message: err.message }), true);
      return;
    }
  }
  // A sort keyed by a property this category lacks no longer applies.
  if (sortKey && !currentColumns().some(c => c.key === sortKey)) {
    sortKey = '';
    sortDir = 'asc';
    // The address bar still names the dropped sort; repair the current entry
    // (added by pushCategoryParam) so it matches the actual order.
    writeSortParams(true);
  }
  // Filters referencing a property this category lacks cannot apply either;
  // drop them and repair the address bar the same way.
  if (sanitizeFilters()) {
    writeFilterParams(true);
  }
  renderHeader();
  renderFilterControls();
  // The startup skeleton was rendered before this category's property
  // columns were known, so its rows have too few cells and the table's
  // column widths move when the real rows arrive. Re-render it with the
  // final column count; real rows then land in a table that already has
  // the right shape.
  clearSkeleton();
  renderSkeleton(PAGE_SIZE);
}

// User-driven category change: refetch properties, then restart the listing.
async function loadPropertiesAndReload() {
  currentTypeId = categorySelect.value;
  await loadProperties();
  closeBuilder();
  resetAndReload();
}

// Rewrite just the ?category= query param in the current URL, replacing the
// browser's history entry so the change is reachable via back/forward.
function pushCategoryParam(id) {
  const url = new URL(window.location.href);
  if (id) {
    url.searchParams.set('category', id);
  } else {
    url.searchParams.delete('category');
  }
  history.pushState({}, '', url);
}

// Write the active full-text query into the URL as ?q=, or drop the param
// when the search box is empty, so a search is shareable and traversable
// exactly like the sort and filter state. replace=true rewrites the current
// history entry (debounced typing); replace=false pushes a new entry
// (explicit commit) — but only when the URL would actually change, so
// re-committing the same query adds no duplicate entry. The query string is
// rebuilt at the raw-string level (not via URLSearchParams) so the existing
// pairs — including the hand-written ':' in filter=<id>:<value> — keep
// their original encoding.
function writeSearchParams(replace) {
  const raw = window.location.search.replace(/^\?/, '');
  const kept = raw === '' ? [] : raw.split('&').filter((p) => p !== '' && p.split('=')[0] !== 'q');
  if (currentQuery) {
    kept.push(`q=${encodeURIComponent(currentQuery)}`);
  }
  const url = new URL(window.location.href);
  url.search = kept.join('&');
  if (replace) {
    history.replaceState({}, '', url);
  } else if (url.href !== window.location.href) {
    history.pushState({}, '', url);
  }
}

// Write the active sort into the URL as ?sort=/?dir=, or drop both params
// when the default order is active, so the address bar always matches the
// table. replace=true rewrites the current history entry (URL repair, e.g.
// after a stale sort is dropped); replace=false adds a new entry so a sort
// click is reachable via back/forward.
function writeSortParams(replace) {
  const url = new URL(window.location.href);
  if (sortKey) {
    url.searchParams.set('sort', sortKey);
    url.searchParams.set('dir', sortDir);
  } else {
    url.searchParams.delete('sort');
    url.searchParams.delete('dir');
  }
  if (replace) {
    history.replaceState({}, '', url);
  } else {
    history.pushState({}, '', url);
  }
}

categorySelect.addEventListener('change', () => {
  pushCategoryParam(categorySelect.value);
  loadPropertiesAndReload();
});

addFilterBtn.addEventListener('click', openBuilder);
filterCancel.addEventListener('click', closeBuilder);
filterApply.addEventListener('click', applyBuilder);
filterProperty.addEventListener('change', renderValueControl);
filterOperator.addEventListener('change', renderValueControl);
filterBuilder.addEventListener('keydown', (e) => {
  if (e.key === 'Escape') closeBuilder();
});

// Apply the search box's current value as the active full-text query and
// reload. replace=true rewrites the current history entry (the debounced
// typing path — one entry per settled search, not per keystroke);
// replace=false pushes a new entry so an explicit commit (Enter, the clear
// button) stays reachable via back/forward.
function searchAndReload(replace) {
  clearTimeout(searchTimer);
  currentQuery = searchInput.value.trim();
  cursor = null;
  hasMore = true;
  loading = false;
  loadGen++;
  if (currentQuery === '') {
    // Back to the full listing: rebuild from scratch.
    tbody.textContent = '';
  }
  writeSearchParams(replace);
  loadPage();
}

searchInput.addEventListener('input', () => {
  clearTimeout(searchTimer);
  searchTimer = setTimeout(() => searchAndReload(true), SEARCH_DELAY_MS);
});

// Enter and the browser's clear ("x") button fire 'search': an explicit
// commit earns its own history entry. When the state is already applied
// (e.g. Enter pressed after the debounce has run) neither a history entry
// nor a reload is needed.
searchInput.addEventListener('search', () => {
  if (searchInput.value.trim() === currentQuery) return;
  searchAndReload(false);
});

window.addEventListener('scroll', onScroll, { passive: true });
window.addEventListener('resize', onScroll);

// Apply the ?category=/?sort=/?dir=/?q= and filter state reflected in the
// current URL back onto the dropdown, sort state, search box, filters, and
// listing. Shared by initial load and browser back/forward so the two always
// agree with the address bar.
async function applyUrlState() {
  const params = new URLSearchParams(window.location.search);
  const id = params.get('category') || '';
  if (String(currentTypeId) !== id) {
    categorySelect.value = id;
    // Take the sort, the search, and the filters from the URL before the
    // reload so loadPropertiesAndReload validates the sort and filters
    // against the new columns.
    sortKey = params.get('sort') || '';
    sortDir = params.get('dir') === 'desc' ? 'desc' : 'asc';
    currentQuery = params.get('q') || '';
    searchInput.value = currentQuery;
    // Filters are read here but validated inside loadPropertiesAndReload
    // once the new category's columns are known: a shared URL's filters
    // belong to the category in the URL, not the previously selected one.
    activeFilters = readFilterParams(params);
    await loadPropertiesAndReload();
    return;
  }
  // Same category: the sort, the search, and the filters can differ between
  // state and URL.
  let sort = params.get('sort') || '';
  const dir = params.get('dir') === 'desc' ? 'desc' : 'asc';
  let sortChanged = sort !== sortKey || (sort && dir !== sortDir);
  if (sort && !currentColumns().some(c => c.key === sort)) {
    // Unknown column (hand-typed or stale URL): the server would reject it,
    // so fall back to the default order and repair the address bar. An
    // active sort being dropped still re-orders the table.
    sortChanged = sortKey !== '';
    sortKey = '';
    sortDir = 'asc';
    updateSortIndicators();
    writeSortParams(true);
    sort = '';
  } else if (sortChanged) {
    sortKey = sort;
    sortDir = dir;
    updateSortIndicators();
  }
  const prevKey = filtersKey(activeFilters);
  activeFilters = readFilterParams(params);
  if (sanitizeFilters()) writeFilterParams(true);
  renderFilterControls();
  const filtersChanged = filtersKey(activeFilters) !== prevKey;
  const prevQuery = currentQuery;
  const query = params.get('q') || '';
  const queryChanged = query !== prevQuery;
  if (queryChanged) {
    currentQuery = query;
    searchInput.value = query;
  }
  if (sortChanged && !filtersChanged && !queryChanged) {
    reloadForSort();
  } else if (sortChanged || filtersChanged || queryChanged) {
    resetAndReload();
  }
}

// Back/forward navigated to another ?category=/?sort= combination.
window.addEventListener('popstate', applyUrlState);

// The toolbar hyperlink points at /login for anonymous visitors and at
// /admin once a valid session cookie exists. On failure the login link is
// shown: without an answer there is nothing to administrate.
async function updateAdminLink() {
  const link = document.getElementById('admin-link');
  if (!link) return;
  let loggedIn = false;
  try {
    const res = await fetch('/api/session');
    if (res.ok) {
      loggedIn = (await res.json()).authenticated === true;
    }
  } catch (err) {
    /* fall through to the login link */
  }
  link.hidden = false;
  link.href = loggedIn ? '/admin' : '/login';
  const key = loggedIn ? 'nav.administration' : 'nav.login';
  link.setAttribute('data-i18n', key);
  link.textContent = t(key);
  // The row action column (delete + AI completion) follows the session.
  authenticated = loggedIn;
  renderHeader();
}

// --- Property provenance tooltip ---------------------------------------------

// Hover tooltip for property cells: shows the value's source URL (clickable,
// opened in a new tab) and its confidence, or a note that the source is
// missing. The provenance lives in data attributes the row builders set
// (see applyProvenance); this reads them via delegation on the table body.
let propTooltip = null;

function ensurePropTooltip() {
  if (propTooltip) return propTooltip;
  propTooltip = document.createElement('div');
  propTooltip.className = 'prop-tooltip';
  propTooltip.hidden = true;
  document.body.appendChild(propTooltip);
  return propTooltip;
}

function showPropTooltip(td) {
  const tip = ensurePropTooltip();
  const src = td.dataset.src || '';
  const conf = td.dataset.conf;
  tip.textContent = '';
  if (src) {
    const label = document.createElement('span');
    label.textContent = 'Source: ';
    const link = document.createElement('a');
    link.href = src;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.textContent = src;
    tip.append(label, link);
    // Confidence is optional provenance; only show a percentage when the
    // value actually carries one.
    if (conf !== undefined && conf !== '' && !Number.isNaN(parseFloat(conf))) {
      const confLabel = document.createElement('span');
      confLabel.className = 'prop-tooltip-conf';
      confLabel.textContent = ` (confidence ${Math.round(parseFloat(conf) * 100)}%)`;
      tip.append(confLabel);
    }
  } else if (td.dataset.na === '1') {
    // The AI agent (or a human) recorded that this property does not apply
    // to the product; the recorded explanation follows.
    const label = document.createElement('span');
    label.className = 'prop-tooltip-missing';
    label.textContent = t('prop.tooltip_not_applicable');
    tip.append(label);
    const note = td.dataset.note || '';
    if (note) {
      tip.append(' ');
      const noteSpan = document.createElement('span');
      noteSpan.className = 'prop-tooltip-note';
      noteSpan.textContent = note;
      tip.append(noteSpan);
    }
  } else if (td.dataset.attempted === '1') {
    // The AI enrichment agent already looked this up and found no verifiable
    // value; clicking the AI button will retry it. When the agent explained
    // why (sites checked, ambiguous sources), the explanation follows.
    const label = document.createElement('span');
    label.className = 'prop-tooltip-missing';
    label.textContent = t('prop.tooltip_no_value');
    tip.append(label);
    const note = td.dataset.note || '';
    if (note) {
      tip.append(' ');
      const noteSpan = document.createElement('span');
      noteSpan.className = 'prop-tooltip-note';
      noteSpan.textContent = note;
      tip.append(noteSpan);
    }
  } else {
    const label = document.createElement('span');
    label.className = 'prop-tooltip-missing';
    label.textContent = 'Source: missing';
    tip.append(label);
  }
  // The tooltip sits flush against the cell's bottom edge (no gap) so the
  // pointer can move onto the link without leaving the cell and dismissing
  // the tooltip mid-move.
  const rect = td.getBoundingClientRect();
  tip.hidden = false;
  const tipRect = tip.getBoundingClientRect();
  let left = rect.left + window.scrollX;
  if (left + tipRect.width > window.scrollX + document.documentElement.clientWidth) {
    left = window.scrollX + document.documentElement.clientWidth - tipRect.width - 4;
  }
  tip.style.left = `${Math.max(4, left)}px`;
  tip.style.top = `${rect.bottom + window.scrollY}px`;
}

function hidePropTooltip() {
  if (propTooltip) propTooltip.hidden = true;
}

function initProvenanceTooltip() {
  tbody.addEventListener('mouseover', (event) => {
    const td = event.target.closest('td');
    if (td && td.dataset.src !== undefined) {
      showPropTooltip(td);
    }
  });
  tbody.addEventListener('mouseout', (event) => {
    const td = event.target.closest('td');
    if (!td || td.dataset.src === undefined) return;
    // Moving into the tooltip itself (e.g. to click the source link) keeps
    // it open; the tooltip hides when the pointer leaves it for anywhere else.
    if (propTooltip && propTooltip.contains(event.relatedTarget)) return;
    hidePropTooltip();
  });
  if (propTooltip) {
    propTooltip.addEventListener('mouseleave', hidePropTooltip);
  }
}

// First paint layout + request start, all before any round-trip completes:
// the URL state (?category/?sort/?q/filters) is read synchronously so every
// first-load request can fire in parallel, and the full header plus a page
// of skeleton rows render so the table occupies (roughly) its loaded height
// from the start. Previously the page waited for window.load and then ran
// session → types → pending → properties → products strictly one after
// another, so real rows arrived only after four-plus serial round-trips.
(function bootstrapUrlState() {
  const params = new URLSearchParams(window.location.search);
  const id = params.get('category') || '';
  currentTypeId = id;
  sortKey = params.get('sort') || '';
  sortDir = params.get('dir') === 'desc' ? 'desc' : 'asc';
  currentQuery = params.get('q') || '';
  searchInput.value = currentQuery;
  activeFilters = readFilterParams(params);
})();

// --- Server-rendered first page ----------------------------------------------
// The document request for this URL is rendered by the server
// (internal/api/home.go): the first page of products, the category's property
// columns, and the listing state arrive inside the HTML. The client adopts
// that state instead of fetching page 1; every later interaction (sort,
// search, filters, scroll) uses the normal fetch path. A missing or
// unparseable state (static fallback, direct /index.html hit) falls back to
// the skeleton + fetch flow.
function readSSRState() {
  const el = document.getElementById('ssr-state');
  if (!el) return null;
  try {
    const state = JSON.parse(el.textContent);
    if (!state || !Array.isArray(state.products) || !Array.isArray(state.properties)) return null;
    return state;
  } catch {
    return null;
  }
}

// Take over the server-rendered page: install the column state, the paging
// cursor, and the match count. The rendered rows are already in the DOM.
function adoptSSRState(state) {
  propertyColumns = state.properties;
  booleanPropertyIds = new Set(state.properties
    .filter((p) => p.property_type === 'boolean').map((p) => p.id));
  const last = state.products[state.products.length - 1];
  cursor = state.cursor || (last ? { brand: last.brand, model: last.model, id: last.id } : null);
  hasMore = Boolean(state.hasMore);
  if (state.total !== null && state.total !== undefined) {
    showCount(state.total, state.search
      ? tCount('count.matches', state.total)
      : (currentTypeId
        ? tCount('count.products_in_category', state.total)
        : tCount('count.products', state.total)));
  }
  renderHeader();
}

// A server-rendered document carries its first page inside the HTML, so the
// client adopts it and only fires the session/types/pending requests. The
// no-SSR fallback renders the header plus a page of skeleton rows and starts
// every first-load request in parallel instead.
const ssrState = readSSRState();
if (ssrState) {
  adoptSSRState(ssrState);
} else {
  renderHeader();
  renderSkeleton(PAGE_SIZE);
}

// Every independent first-load request starts here, together. The session
// result must still be settled before rows render (editable cells and the
// action column follow it), and pending state likewise, so both are awaited
// below before applyUrlState/loadPage build any rows. On an SSR page the
// columns came with the document, so the properties request is skipped —
// it would only re-render the header and append skeleton rows.
const sessionReady = updateAdminLink();
const typesReady = loadTypes();
const propertiesReady = ssrState ? Promise.resolve() : loadProperties();
const pendingReady = fetchPending();

(async () => {
  await Promise.all([sessionReady, typesReady, propertiesReady, pendingReady]);
  initProvenanceTooltip();
  if (pendingIds.size > 0) {
    pollIdleTicks = 0;
    pollPendingLoop();
  }
  if (ssrState) {
    // Register the server-rendered products so the pending poller can refresh
    // their rows, then hand logged-in users their editable cells and row
    // actions: the SSR markup is anonymous-style, and the rebuild is
    // geometry-identical so it causes no layout shift.
    for (const p of ssrState.products) {
      loadedProducts.set(String(p.id), p);
    }
    if (authenticated) {
      tbody.textContent = '';
      for (const p of ssrState.products) {
        addRow(p);
      }
    }
    for (const p of ssrState.products) {
      if (pendingIds.has(String(p.id))) {
        const tr = tbody.querySelector(`tr[data-id="${CSS.escape(String(p.id))}"]`);
        if (tr) updateRowPending(tr, p);
      }
    }
    if (ssrState.search) {
      setStatus(ssrState.products.length === 0 ? t('status.no_matches') : '');
    } else {
      setStatus(hasMore ? t('status.scroll_more') : t('status.all_loaded'));
      fillViewport();
    }
  }
  await applyUrlState();
  // The SSR page already shows page 1; only the fallback path must fetch it.
  if (!ssrState) loadPage();
})();